import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  Bookmark,
  Clock,
  Download,
  Globe,
  LayoutGrid,
  MessageSquareWarning,
  PencilLine,
} from 'lucide-react';
import { ComponentType, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import useSearchParamActions from '@/customHooks/useSearchParamActions';
import { selectUserId } from '@/redux/auth/auth.slice';
import { useGetUserByUsernameQuery } from '@/redux/auth/auth.api.slice';

/** `view` tabs clear postType; `postType` tabs pin view to userPosts. */
const TABS: {
  key: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  kind: 'view' | 'postType';
}[] = [
  { key: 'userPosts', label: 'My Posts', icon: LayoutGrid, kind: 'view' },
  { key: 'draft', label: 'Draft', icon: PencilLine, kind: 'postType' },
  { key: 'public', label: 'Published', icon: Globe, kind: 'postType' },
  {
    key: 'private',
    label: 'Pending for Review',
    icon: Clock,
    kind: 'postType',
  },
  {
    key: 'rejected_with_comments',
    label: 'Required Edits',
    icon: MessageSquareWarning,
    kind: 'postType',
  },
  { key: 'bookmarks', label: 'Bookmarks', icon: Bookmark, kind: 'view' },
  {
    key: 'downloadLogs',
    label: 'Download Logs',
    icon: Download,
    kind: 'view',
  },
];

function FilterBar() {
  const { appendMultipleSearchParams, removeAllSearchParams, getSearchParam } =
    useSearchParamActions();
  // Hosted in the navbar, so the profile is resolved here rather than passed in
  // (RTK Query dedupes — ProfilePage already runs this). Usernames resolve
  // case-insensitively server-side, so ownership compares ids, not strings.
  const { username } = useParams();
  const { data: profileUser } = useGetUserByUsernameQuery(username ?? '');
  const currentUserId = useSelector(selectUserId);

  const postType = getSearchParam('postType');
  const view = getSearchParam('view');

  function handleViewChange(viewType: string) {
    removeAllSearchParams(['postType', 'view']);
    appendMultipleSearchParams([
      { key: 'view', value: viewType, replace: true },
    ]);
  }

  function handlePostTypeChange(postVisibility: string) {
    removeAllSearchParams(['postType', 'view']);
    appendMultipleSearchParams([
      {
        key: 'postType',
        value: postVisibility,
        replace: true,
      },
      {
        key: 'view',
        value: 'userPosts',
        replace: true,
      },
    ]);
  }

  useEffect(() => {
    appendMultipleSearchParams([
      {
        key: 'view',
        value: 'userPosts',
        replace: true,
      },
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!profileUser || currentUserId !== profileUser._id) return null;

  return (
    <div className="flex flex-wrap items-center gap-2">
      {TABS.map(({ key, label, icon: Icon, kind }) => {
        // A view tab only lights up with no postType set — picking one always
        // lands on userPosts, which would otherwise light that tab too.
        const isActive =
          kind === 'postType'
            ? postType === key
            : view === key && postType === undefined;

        return (
          <Button
            key={key}
            variant={isActive ? 'default' : 'outline'}
            title={label}
            aria-label={label}
            onClick={() =>
              kind === 'postType'
                ? handlePostTypeChange(key)
                : handleViewChange(key)
            }
            className="px-2.5 sm:px-4"
          >
            <Icon className="h-4 w-4 sm:mr-2" />
            {/* Narrow screens stack seven labelled buttons into a column
                taller than the feed; the icons alone stay on one or two rows. */}
            <span className="hidden sm:inline">{label}</span>
          </Button>
        );
      })}
    </div>
  );
}

export default FilterBar;
